import { useAuthContext } from '@/context/AuthContext';
import { useState } from 'react';
import {gql} from '@apollo/client';
import {useMutation} from '@apollo/client/react';
import errorToText from '@/utils/errorToText';

// Define the GraphQL mutations
const UPDATE_USER_RETRIBUTION = gql`
  mutation updateRetribution($uuid: ID!, $retributionModelUuid: ID!) {
    updateUser(updatedUser: {uuid: $uuid, retributionModelUuid: $retributionModelUuid}) {
      retributionModel {
        uuid
        upToTenConsultants
        upToFiveConsultants
        moreThanTenConsultants
      }
    }
  }
`;

const UPDATE_USER_LANGUAGE = gql`
  mutation updateLanguage($uuid: ID!, $language: InterfaceLanguage!) {
    updateUser(updatedUser: {uuid: $uuid, language: $language}) {
      language
    }
  }
`;

const UPDATE_USER_IBAN = gql`
  mutation updateIban($uuid: ID!, $iban: String!) {
    updateUser(updatedUser: {uuid: $uuid, iban: $iban}) {
      iban
    }
  }
`;

const UPDATE_USER_BIC = gql`
  mutation updateBic($uuid: ID!, $bicCode: String!) {
    updateUser(updatedUser: {uuid: $uuid, bicCode: $bicCode}) {
      bicCode
    }
  }
`;

const UPDATE_USER_PHONE_NUMBER = gql`
  mutation updatePhoneNumber($uuid: ID!, $phoneNumber: String!) {
    updateUser(updatedUser: {uuid: $uuid, phoneNumber: $phoneNumber}) {
      phoneNumber
    }
  }
`;

const UPDATE_USER_PERSONAL_EMAIL = gql`
  mutation updatePersonalEmail($uuid: ID!, $personalEmail: String!) {
    updateUser(updatedUser: {uuid: $uuid, personalEmail: $personalEmail}) {
      personalEmail
    }
  }
`;

const UPDATE_USER_ADDRESS = gql`
  mutation updateAddress(
    $uuid: ID!, 
    $addressLine1: String!, 
    $addressLine2: String, 
    $zipCode: String!, 
    $city: String!, 
    $countryCode: String!
  ) {
    updateUser(updatedUser: {
      uuid: $uuid, 
      addressLine1: $addressLine1, 
      addressLine2: $addressLine2, 
      zipCode: $zipCode, 
      city: $city, 
      countryCode: $countryCode
    }) {
      addressLine1
      addressLine2
      zipCode
      city
      countryCode
    }
  }
`;

const UPDATE_USER_FIDUCIARY_ID = gql`
  mutation updateFiduciaryId($uuid: ID!, $fiduciaryId: String!) {
    updateUser(updatedUser: {uuid: $uuid, fiduciaryId: $fiduciaryId}) {
      fiduciaryId
    }
  }
`;

const UPDATE_USER_LEAVE_DATE = gql`
  mutation updateLeaveDate($uuid: ID!, $leaveDate: String) {
    updateUser(updatedUser: {uuid: $uuid, leaveDate: $leaveDate}) {
      leaveDate
    }
  }
`;

const useUpdateUser = () => {
    const [response, setResponse] = useState(null);

    const [updateRetribution, { loading: loadingRetribution, error: errorRetribution, data: dataRetribution }] = useMutation(UPDATE_USER_RETRIBUTION, {
        onError: (error) => {
            console.error(error);
        },
    });

    const [updateLanguage, { loading: loadingLanguage, error: errorLanguage, data: dataLanguage }] = useMutation(UPDATE_USER_LANGUAGE, {
        onError: (error) => {
            console.error(error);
        },
    });

    const setRetribution = async (uuid, retributionModelUuid) => {
        try {
            const { data } = await updateRetribution({
                variables: { uuid, retributionModelUuid },
            });
            setResponse(data.updateUser);
            return { data: data.updateUser, error: null };
        } catch (err) {
            return { data: null, error: err.message };
        }
    };

    const setLanguage = async (language) => {
        const uuid = authUser.uuid;
        try {
            const { data } = await updateLanguage({
                variables: { uuid, language },
            });
            setResponse(data.updateUser);
            return { data: data.updateUser, error: null };
        } catch (err) {
            return { data: null, error: err.message };
        }
    };

    const [updateIban, { loading: loadingIban, error: errorIban, data: dataIban }] = useMutation(UPDATE_USER_IBAN);

    const [updateBic, { loading: loadingBic, error: errorBic, data: dataBic }] = useMutation(UPDATE_USER_BIC);

    const [updatePhoneNumber, { loading: loadingPhoneNumber, error: errorPhoneNumber, data: dataPhoneNumber }] = useMutation(UPDATE_USER_PHONE_NUMBER);

    const [updatePersonalEmail, { loading: loadingPersonalEmail, error: errorPersonalEmail, data: dataPersonalEmail }] = useMutation(UPDATE_USER_PERSONAL_EMAIL);

    const [updateAddress, { loading: loadingAddress, error: errorAddress, data: dataAddress }] = useMutation(UPDATE_USER_ADDRESS, {
        onError: (error) => {
            console.error(error);
        },
    });

    const [updateFiduciaryId, { loading: loadingFiduciaryId, error: errorFiduciaryId, data: dataFiduciaryId }] = useMutation(UPDATE_USER_FIDUCIARY_ID);

    const [updateLeaveDate, { loading: loadingLeaveDate, error: errorLeaveDate, data: dataLeaveDate }] = useMutation(UPDATE_USER_LEAVE_DATE);

    return {
        setRetribution,
        setLanguage,
        updateIban,
        updateBic,
        updatePhoneNumber,
        updatePersonalEmail,
        updateAddress,
        updateFiduciaryId,
        updateLeaveDate,
        uuidResponse: response,
        // Export loading, error, data for each mutation
        loadingRetribution, errorRetribution, dataRetribution,
        loadingLanguage, errorLanguage, dataLanguage,
        loadingIban, errorIban, dataIban,
        loadingBic, errorBic, dataBic,
        loadingPhoneNumber, errorPhoneNumber, dataPhoneNumber,
        loadingPersonalEmail, errorPersonalEmail, dataPersonalEmail,
        loadingAddress, errorAddress, dataAddress,
        loadingFiduciaryId, errorFiduciaryId, dataFiduciaryId,
        loadingLeaveDate, errorLeaveDate, dataLeaveDate,
    };
};

export default useUpdateUser;
